import { useState, useEffect } from "react";
import { ActionMenu, ActionList, Button, Text, Stack, FormControl } from "@primer/react";
import { ChevronLeftIcon } from '@primer/octicons-react';
import { DateRangeInput } from "../../../../basic/inputs/DateRangeInput";

interface DateFormModalProps {
    dateFilterOpened: boolean;
    setDateFilterOpened: (value: boolean) => void;
    fromDate: string;
    setFromDate: (value: string) => void;
    toDate: string;
    setToDate: (value: string) => void;
    dateFormError: boolean;
    onDismiss: () => void;
    onApply: () => void;
    onClearDates?: () => void;
    isShowBackButton: boolean
    mode?: 'full' | 'monthYear';
}

const DateFormModal: React.FC<DateFormModalProps> = ({
    dateFilterOpened,
    setDateFilterOpened,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    dateFormError,
    onDismiss,
    onApply,
    onClearDates = undefined,
    isShowBackButton = true,
    mode = "full"
}) => {
    const [initialValues, setInitialValues] = useState({ fromDate: '', toDate: '' });

    useEffect(() => {
        if (dateFilterOpened) {
            setInitialValues({ fromDate, toDate });
        }
    }, [dateFilterOpened]);

    const dismiss = () => {
        // Reset vals
        setFromDate(initialValues.fromDate);
        setToDate(initialValues.toDate);

        onDismiss();
    }

    const clearForm = () => {
        if (onClearDates) {
            onClearDates();
        } else {
            setFromDate('');
            setToDate('');
        }
    }

    const resetForm = () => {
        clearForm()
        setInitialValues({ fromDate: '', toDate: '' });
    }

    return (
        <ActionMenu open={dateFilterOpened} onOpenChange={setDateFilterOpened}>
            <ActionMenu.Button className="w-8 h-8 right-[16px] absolute ml-auto invisible"></ActionMenu.Button>
            <ActionMenu.Overlay className="!w-[320px]">
                <ActionList>
                    <Stack justify="space-between" direction="horizontal">
                        <Stack direction="horizontal">
                            {isShowBackButton ? (<Button
                                leadingVisual={ChevronLeftIcon}
                                variant='invisible'
                                onClick={dismiss}>
                                <Text className='block mb-[1.5px]'>По дате размещения</Text>
                            </Button>) : (<Text className='block mb-[8px] ml-4'>По дате размещения</Text>)}
                        </Stack>
                    </Stack>
                    <Stack className='ml-4 mr-4'>
                        <DateRangeInput
                            fromDate={fromDate}
                            toDate={toDate}
                            onFromDateChange={setFromDate}
                            onToDateChange={setToDate}
                            error={dateFormError}
                            mode={mode}
                        />
                        {dateFormError && (
                            <FormControl.Validation variant="error" className='mt-[1px]'>Вы ввели некорректные значения</FormControl.Validation>
                        )}
                        {dateFormError && (
                            <FormControl.Validation variant="error" className='mt-[1px]'>Вы ввели некорректные значения</FormControl.Validation>
                        )}
                    </Stack>
                    <Stack direction="horizontal" gap='normal' justify='end' className="mt-[22px] mr-4">
                        <div></div>
                        <Button
                            disabled={(fromDate.trim() || toDate.trim()) ? false : true}
                            onClick={resetForm}
                            size='small'
                            variant="default">
                            Очистить
                        </Button>
                        <Button
                            onClick={onApply}
                            size='small'
                            variant="primary">
                            Применить
                        </Button>
                    </Stack>
                </ActionList>
            </ActionMenu.Overlay>
        </ActionMenu>
    )
}

const formatDateRange = (fromDate: string, toDate: string, mode?: 'full' | 'monthYear'): string => {
    const formatDate = (dateString: string): string => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);

            if (mode === 'monthYear') {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                return `${year}-${month}`;
            }

            // Режим 'full' или по умолчанию
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = String(date.getFullYear()).slice(-2);
            return `${day}.${month}.${year}`;
        } catch {
            return '';
        }
    };

    const formattedFrom = formatDate(fromDate);
    const formattedTo = formatDate(toDate);

    if (formattedFrom && formattedTo) {
        return `${formattedFrom} - ${formattedTo}`;
    } else if (formattedFrom) {
        return `от ${formattedFrom}`;
    } else if (formattedTo) {
        return `до ${formattedTo}`;
    }

    return '';
};

export { DateFormModal, formatDateRange }